import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";

import HeroStory from "@/components/story/HeroStory";
import ProblemScene from "@/components/story/ProblemScene";
import MethodScene from "@/components/story/MethodScene";
import WorkScene from "@/components/story/WorkScene";
import PhilosophyScene from "@/components/story/PhilosophyScene";
import { WRAP } from "@/components/story/layout";

import BeforeScene from "@/components/scenes/BeforeScene";
import IndustryExplorer from "@/components/scenes/IndustryExplorer";
import TrustScene from "@/components/scenes/TrustScene";

import { Reveal, Item, MaskLines } from "@/components/Reveal";
import { FAQ } from "@/lib/faq";
import { SITE, waLink } from "@/lib/site";

/* Three on the homepage, not five. The rest live on /questions; a wall of
   answers on the landing page reads as work, not as reassurance. */
const HOME_FAQ = FAQ.slice(0, 3);

const trustCells = [
  ["Founder-led, engineer-delivered", "The people you meet on the call are the people who build."],
  ["Built for production", "Permissions, approvals and audit trails are part of the system."],
  ["You own all of it", "Code, data, accounts. If we part ways, it keeps running."],
];

/**
 * The homepage as one narrative, in six moments:
 *
 *   hero (complexity becoming clarity)
 *   01 the problem
 *   02 the method
 *   03 what we build
 *   04 today and after     (BeforeScene: the strongest argument on the site)
 *      pick your industry  (IndustryExplorer: the second conversion path)
 *   05 what we believe
 *      who runs this, the first three questions, and the ask
 *
 * MessScene, CallScene and Outcomes are no longer imported here. The files are
 * untouched and still work; the method section now covers the same ground in a
 * fifth of the scroll. Put them back if the shorter page tests worse.
 */
export default function Home() {
  return (
    <div data-testid="page-home">
      <Seo
        title="AUT labs · AI automation and custom software in Mumbai"
        description="AUT labs is an AI automation studio in Mumbai. We build AI agents, business automation, and custom ERP and CRM systems for Indian businesses."
        faq={HOME_FAQ}
      />

      <HeroStory />

      <div id="how" className="scroll-mt-20">
        <ProblemScene />
      </div>

      <MethodScene />
      <WorkScene />

      <BeforeScene />
      <IndustryExplorer />

      <PhilosophyScene />
      <TrustScene />

      {/* Who runs this. Kept short; the site is the proof. */}
      <section id="about" className="border-b border-ink col-rules scroll-mt-20" data-testid="section-about">
        <div className={`${WRAP} section-pad`}>
          <Reveal className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <Item as="p" className="eyebrow">Who runs this</Item>
              <h2 className="display mt-5 text-[32px] md:text-[44px] leading-[1]">
                <MaskLines lines={["The engineers you meet", "are the engineers who build."]} />
              </h2>
              <Item as="p" className="mt-6 display text-[22px] md:text-[26px]" data-testid="team-names">
                {SITE.teamNames.join(" · ")}
              </Item>
              <Item as="p" className="mt-3 text-[15px] text-ink/70">{SITE.location}. On site when it helps.</Item>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 border-t border-l border-ink">
              {trustCells.map((c) => (
                <Item key={c[0]} className="border-r border-b border-ink p-5 md:p-6">
                  <h3 className="display text-[22px] leading-[1.15]">{c[0]}</h3>
                  <p className="text-[15px] text-ink/75 mt-3">{c[1]}</p>
                </Item>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Before you ask. Full list lives on /questions; this is the first three. */}
      <section className="border-b border-ink" data-testid="section-questions">
        <div className={`${WRAP} section-pad`}>
          <p className="eyebrow">Before you ask</p>
          <div className="mt-8 border-t border-ink">
            {HOME_FAQ.map((f, i) => (
              <Reveal key={f.q} className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-8 py-7 md:py-8 border-b border-ink items-start">
                <Item className="md:col-span-5">
                  <h3 className="display text-2xl md:text-[30px] leading-[1.08]" data-testid={`home-q-${i}`}>{f.q}</h3>
                </Item>
                <Item className="md:col-span-7"><p className="text-lg text-ink/85">{f.a}</p></Item>
              </Reveal>
            ))}
          </div>
          <div className="mt-6">
            <Link to="/questions" className="ember-link text-ember-deep text-lg" data-testid="home-questions-link">The rest of the questions →</Link>
          </div>
        </div>
      </section>

      {/* Final CTA. The one cobalt block on the page. */}
      <section className="bg-cobalt text-paper" data-testid="section-cta">
        <div className={`${WRAP} story-pad`}>
          <Reveal className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7">
              <Item as="p" className="story-index story-index--paper">06 / The next step</Item>
              <h2 className="display story-h2 mt-8">
                <MaskLines lines={["Fifteen minutes.", "One clear answer."]} />
              </h2>
              <Item as="p" className="mt-7 max-w-[46ch] story-lead text-paper/85">
                Tell us what you are trying to build, fix or automate. We will tell you
                what is worth doing first, what it saves, and honestly when the answer
                is nothing.
              </Item>
              <Item as="ol" className="mt-9 border-t border-paper/30 max-w-[46ch]">
                {[
                  ["On the call", "You show us how the work actually moves."],
                  ["Same call", "We name the first thing worth building, and what it saves."],
                  ["After", "A written plan. No obligation, no deck, nothing to sign."],
                ].map(([when, what]) => (
                  <li key={when} className="flex gap-4 py-3 border-b border-paper/20">
                    <span className="eyebrow w-[86px] shrink-0" style={{ color: "inherit" }}>{when}</span>
                    <span className="text-[15px] text-paper/85">{what}</span>
                  </li>
                ))}
              </Item>
            </div>
            <Item className="md:col-span-5 flex flex-col items-start gap-4 md:border-l md:border-paper/30 md:pl-10">
              <Link to="/book" className="pill pill-paper group" data-testid="cta-talk">
                Book a 15-minute call <ArrowRight size={16} className="cta-arrow" aria-hidden="true" />
              </Link>
              <Link to="/find-opportunities" className="ember-link text-paper text-lg" data-testid="cta-analyse">
                Or see your opportunities first, free →
              </Link>
              <a href={`mailto:${SITE.email}`} className="text-paper/90 text-[15px] ember-link">
                {SITE.email}
              </a>
              <a href={waLink} target="_blank" rel="noreferrer" className="text-paper/90 text-[15px] ember-link" data-testid="cta-wa">
                Or WhatsApp {SITE.phoneDisplay}
              </a>
              <p className="text-[13px] text-paper/85 max-w-[32ch] mt-1">
                {SITE.area}, {SITE.location}. Free. We are engineers, not a sales team.
                If technology is not the answer for you, we will say so on the call.
              </p>
            </Item>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
